import { MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { UserInfoDto, UserInfoDtoPaginatedList, UserOperationSettingsResponse, UserOperationType, UserSettingsDto } from "generated/CoreApi_v1";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BatchEditSearchUsers, IBatchOperationFilter } from "views/foundation/user/components/BatchEdit/BatchEditSearchUsers";
import { FilterCmd } from "views/general/AdvancedFilter/FilterCmd";
import { OPERATION_PAGE_SIZE } from "views/lms/enrollment/Home";

////////////////////////////////////////////////////////////////////////////

interface IProps {
  onReloadSelectedUsers: () => void;
  settings: UserOperationSettingsResponse;
}

////////////////////////////////////////////////////////////////////////////

export const SearchUsersBox: React.FC<IProps> = ({ onReloadSelectedUsers, settings }) => {

  const { t } = useTranslation();
  const api = useCoreApi();

  const [isImportedFile, setIsImportedFile] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>();
  const [viewData, setViewData] = useState<UserInfoDtoPaginatedList>();
  const [loading, setLoading] = useState<boolean>(false);
  const [posting, setPosting] = useState<boolean>(false);
  const [viewFilter, setViewFilter] = useState<IBatchOperationFilter>({
    pageNumber: 1,
    filter: {
      searchText: "",
      advFilters: [],
      order: null
    },
  });

  ///
  const resetFilter = () => {
    setViewFilter({
      pageNumber: 1,
      filter: {
        searchText: "",
        advFilters: [],
        order: null
      },
    });
  };

  const buildFilter = {
    operation: UserOperationType.BroadcastMessage,
    filter: viewFilter.filter.advFilters,
    searchText: viewFilter.filter.searchText,
    useImportedUsers: isImportedFile,
    action: null,
    objectId: null,
  };

  ///
  const addAllUsers = () => {
    setPosting(false);
    api.v1.userOperation.addAllUsers({ ...buildFilter })
      .then(response => {
        if (response.data) {
          onReloadSelectedUsers();
        }
        else {
          enqueueSnackbar(t("User.BatchEdit.OperationFailMessage"), { variant: "error" });
        }
      })
      .catch(e => MprcRequestSnackbarErrorReport(e))
      .finally(() => setPosting(false));
  };

  ///
  const addUser = (item: UserInfoDto) => {
    setPosting(true);
    api.v1.userOperation.addUser({
      operation: UserOperationType.BroadcastMessage,
      userIds: [item.userId]
    }).then(response => {
      if (response.data) {
        onReloadSelectedUsers();
      }
      else {
        enqueueSnackbar(t("User.BatchEdit.OperationFailMessage"), { variant: "error" });
      }
    }).catch(e => MprcRequestSnackbarErrorReport(e))
      .finally(() => setPosting(false));
  };

  ///
  const clearImportedUser = (onSuccess?: () => void) => {
    setPosting(true);

    api.v1.userOperation.clearImportedUsers({
      operation: UserOperationType.BroadcastMessage,
    }).then(result => {
      if (result.data) {
        setIsImportedFile(false);
        reloadData();
      }
      else {
        enqueueSnackbar(t("User.BatchEdit.OperationFailMessage"), { variant: "error" });
      }
    }).catch(e => {
      MprcRequestSnackbarErrorReport(e);
    }).finally(() => setPosting(false));
  };

  const getData = (pageSize?: number) => {
    api.v1.userOperation.getSelectableUsers({
      ...buildFilter,
      pageNumber: viewFilter.pageNumber,
      pageSize: pageSize ?? OPERATION_PAGE_SIZE,
    }).then(response => {
      setViewData(response.data);
    })
      .catch(e => {
        setViewData(undefined);
        setViewError(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const reloadData = () => {
    resetFilter();
    getData();
  };

  /////
  useEffect(() => {
    getData();
  }, [viewFilter]);


  ///
  const handleOnCompleteImport = (success: boolean) => {
    if (success) {
      onReloadSelectedUsers();
      setIsImportedFile(success);
      reloadData();
    }
  };


  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////


  return (
    <>
      <MprcRequestIndicator visible={loading || posting} type={posting ? "post" : "request"} />
      <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

      <BatchEditSearchUsers
        viewData={viewData}
        onAddUser={addUser}
        onAddAllUsers={addAllUsers}
        onClearImportedUsers={clearImportedUser}
        viewFilter={viewFilter}
        importedFile={isImportedFile}
        onChangeFilter={filter => setViewFilter(state => ({ ...state, ...filter }))}
        onResetFilter={resetFilter}
        onImportList={users => api.v1.userOperation.importUserList({
          operation: UserOperationType.BroadcastMessage,
          filtered: isImportedFile,
          userAuthFieldValues: users
        })}
        onImportFile={fileId => api.v1.userOperation.importUserFile({
          operation: UserOperationType.BroadcastMessage,
          filtered: isImportedFile,
          fileId: fileId
        })}
        onCompleteImport={handleOnCompleteImport}
        filterCommands={[
          ...FilterCmd.User.unitType(settings.unitTypes, 3),
          FilterCmd.User.group(4),
          FilterCmd.User.roles(4),
          FilterCmd.User.permission(4),
          FilterCmd.User.channelMember(4),
          FilterCmd.User.topicOptional(4),
          FilterCmd.User.topicRequired(4),
          FilterCmd.User.hasSealProvenKnowledgeTopic(4),
          FilterCmd.User.isSpecialistInTopic(4),

          FilterCmd.User.trainingApproved(5),
          FilterCmd.User.trainingReproved(5),
          FilterCmd.User.trainingAttending(5),
          FilterCmd.User.trainingAudience(5),
          FilterCmd.User.trainingMandatoryAudience(5),

          FilterCmd.User.isManager(6),
          FilterCmd.User.managerDirectSubordinates(6),
          FilterCmd.User.managerAllSubordinates(6),

          FilterCmd.User.workplace(7, { workplaceLabel: settings.workplaceLabel, isWorkplaceFreeText: settings.isWorkplaceFreeText } as UserSettingsDto),
          FilterCmd.User.tag(7),
          FilterCmd.User.interestTag(7),
          FilterCmd.User.customField(7),

          FilterCmd.User.internals(8),
          FilterCmd.User.externals(8),
          FilterCmd.User.onlyUnblockedUsers(8),
          FilterCmd.User.onlyBlockedUsers(8),

          FilterCmd.User.lastAccessBeforeOf(9),
          FilterCmd.User.lastAccessAfterOf(9),
        ]}
      />
    </>
  );
};
